import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtUSD, fmtDate, fmtCarat } from "@/lib/format";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Gem, Trash2 } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { TableSummaryRow } from "@/components/TableSummaryRow";
import { useAuth } from "@/context/AuthContext";

export default function Jewelry() {
    const { canWrite } = useAuth();
    const [rows, setRows] = useState([]);
    const [status, setStatus] = useState("active");   // 'active' = in_production + available (default). Sold hidden.
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            let params = {};
            if (status === "active") params = { include_sold: false };
            else if (status === "all") params = { include_sold: true };
            else params = { status };
            const { data } = await api.get("/jewelry", { params });
            setRows(data);
        } catch { toast.error("Failed to load"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [status]);

    const remove = async (j) => {
        if (j.status === "sold") { toast.error("Sold jewelry cannot be deleted"); return; }
        if (!confirm(`Delete "${j.name || j.jewelry_type}"? This also deletes its expenses and returns the source parcel to Available.`)) return;
        try {
            await api.delete(`/jewelry/${j.id}`);
            toast.success("Jewelry deleted");
            load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Delete failed"); }
    };

    return (
        <div className="space-y-6 animate-fade-up" data-testid="jewelry-page">
            <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Inventory</div>
                    <h1 className="font-display text-4xl sm:text-5xl leading-none tracking-tight">Jewelry</h1>
                    <p className="text-sm text-stone-500 mt-2">Jewelry pieces are created from parcels. Head to a parcel to set it into a piece.</p>
                </div>
                <div className="w-56">
                    <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger className="rounded-none bg-white border-stone-200" data-testid="jewelry-status-filter"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#faf7ef] border-stone-200 rounded-none">
                            <SelectItem value="active">Active (hide sold)</SelectItem>
                            <SelectItem value="in_production">In production</SelectItem>
                            <SelectItem value="available">Ready to sell</SelectItem>
                            <SelectItem value="sold">Sold</SelectItem>
                            <SelectItem value="all">All statuses</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </header>

            {loading ? (
                <div className="text-stone-500 text-sm">Loading…</div>
            ) : rows.length === 0 ? (
                <div className="border border-stone-200 p-10 text-center bg-[#f5efe0]">
                    <Gem className="mx-auto h-8 w-8 text-stone-500" strokeWidth={1.2} />
                    <h3 className="font-display text-2xl mt-4">No jewelry yet</h3>
                    <p className="text-sm text-stone-500 mt-1">Open a parcel and hit <em>Set into jewelry</em>.</p>
                </div>
            ) : (
                <div className="border border-stone-200 overflow-x-auto" data-testid="jewelry-table">
                    <table className="w-full text-sm min-w-[900px]">
                        <thead>
                            <tr className="border-b border-stone-200 text-[10px] uppercase tracking-widest text-stone-500 text-left">
                                <th className="py-3 px-4">Name / Type</th>
                                <th className="py-3 px-4">Gold</th>
                                <th className="py-3 px-4">Parcel</th>
                                <th className="py-3 px-4 text-right">Total carat</th>
                                <th className="py-3 px-4">Status</th>
                                <th className="py-3 px-4">Created</th>
                                <th className="py-3 px-4 text-right">Total cost</th>
                                <th className="py-3 px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(j => (
                                <tr key={j.id} className="border-b border-stone-200/70 hover:bg-stone-100" data-testid={`jewelry-row-${j.id}`}>
                                    <td className="py-3 px-4">
                                        <Link to={`/jewelry/${j.id}`} className="text-stone-900 hover:text-emerald-700">{j.name || j.jewelry_type}</Link>
                                        <div className="text-[11px] text-stone-500">{j.jewelry_type}</div>
                                    </td>
                                    <td className="py-3 px-4 text-stone-700">{j.gold_karat}k {j.gold_color} · {j.gold_weight_gr}g{j.gross_weight_gr != null ? ` · gross ${j.gross_weight_gr}g` : ""}</td>
                                    <td className="py-3 px-4">
                                        <Link to={`/parcels/${j.parcel_id}`} className="font-mono text-stone-700 hover:text-emerald-700 text-xs underline decoration-stone-300 hover:decoration-emerald-500" data-testid={`jewelry-parcel-link-${j.id}`}>
                                            {j.parcel_snapshot?.code}
                                        </Link>
                                    </td>
                                    <td className="py-3 px-4 text-right num text-stone-700">{fmtCarat(j.parcel_snapshot?.total_carat)}</td>
                                    <td className="py-3 px-4"><StatusBadge status={j.status} kind="jewelry" /></td>
                                    <td className="py-3 px-4 text-stone-500">{fmtDate(j.created_at)}</td>
                                    <td className="py-3 px-4 text-right num">{fmtUSD(j.total_cost ?? j.allocated_cost)}</td>
                                    <td className="py-3 px-4 text-right">
                                        {canWrite && (
                                            <button onClick={() => remove(j)} disabled={j.status === "sold"} className="text-stone-400 hover:text-rose-600 disabled:opacity-30 disabled:hover:text-stone-400" data-testid={`delete-jewelry-${j.id}`} title={j.status === "sold" ? "Sold jewelry cannot be deleted" : "Delete jewelry"}>
                                                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <TableSummaryRow
                            rows={rows}
                            testid="jewelry-summary-row"
                            cells={[
                                { type: "label", text: `${rows.length} rows` },
                                null, null,
                                { type: "sum-carat", get: r => r.parcel_snapshot?.total_carat, testid: "jewelry-summary-carat" },
                                null, null,
                                { type: "sum-usd", get: r => r.total_cost ?? r.allocated_cost, testid: "jewelry-summary-cost" },
                                null,
                            ]}
                        />
                    </table>
                </div>
            )}
        </div>
    );
}
